import AuthProvider from '../common/services/auth/AuthProvider/Index';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const buildAuthorizedRequest = async (
  url: string,
  options: RequestInit = {}
) => {
  const token = await AuthProvider.getValidAuthorizationToken();
  return {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  };
};

const authorizedJsonFetch = async <T>(
  url: string,
  options: RequestInit = {},
  emptyResponseFallback?: T
): Promise<T> => {
  const response = await fetch(url, await buildAuthorizedRequest(url, options));

  if (!response.ok) {
    let errorBody: any = null;
    let errorMessage = `Request failed with status ${response.status}`;

    try {
      errorBody = await response.json();
      if (errorBody && typeof errorBody.message === "string") {
        errorMessage = errorBody.message;
      } else if (errorBody && typeof errorBody.error === "string") {
        errorMessage = errorBody.error;
      }
    } catch (jsonError) {
      console.warn(
        `Could not parse error response body for status ${response.status}:`,
        jsonError
      );
      errorMessage = `Request failed with status ${response.status}: ${response.statusText}`;
    }

    throw new HttpError(errorMessage, response.status, errorBody);
  }

  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return (emptyResponseFallback ?? ({} as T));
  }

  try {
    return await response.json();
  } catch {
    return (emptyResponseFallback ?? ({} as T));
  }
};

// Generic request function with authentication
async function authenticatedRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await AuthProvider.getValidAuthorizationToken();

  const headers = {
    'Content-Type': 'application/json', // Good practice to set Content-Type
    ...options.headers,
    Authorization: `Bearer ${token}`
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers
  });

  // Improved error handling
  if (!response.ok) {
    let errorBody;
    try {
      errorBody = await response.json();
    } catch (e) {
      // Ignore if response is not JSON
      console.error("API Error Response: JSON parsing failed", { status: response.status, body: e });
    }
    console.error("API Error Response:", { status: response.status, body: errorBody });
    throw new Error(`API error: ${response.status} - ${errorBody?.error || response.statusText}`);
  }

  // Handle potential empty responses for 204 No Content etc.
  if (response.status === 204) {
    return {} as T; // Or return null/undefined based on expected behavior
  }

  return await response.json();
}

export interface ApiResponseWrapper<T> {
  success: boolean;
  message: string;
  data: T; // The actual payload is nested within 'data'
}

export interface QLDBResultData {
  id: string; // Example: QLDB document ID
  result: string | null; // Timestamp of the analysis
}

/**
 * Represents the response from the new enriched result endpoint.
 */
export interface EnrichedResultResponse {
  kit_serial: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  customer_email: string | null;
  customer_birthdate?: string | null;
  vasectomy_date?: string | null;
  result: QLDBResultData | null; // The actual test result data from QLDB
  // Additional metadata for comprehensive results display
  collection_date_time?: string | null;
  results_timestamp?: number | null;
  procedure_date?: string | null;
  kit_id?: string | null;
}

// Provider API endpoints
export interface PaginatedResponse<T> {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
  from: number;
  to: number;
  items: T[];
}

export interface Kit {
  serial_number: string;
  customer_email: string;
  status: string;
  has_results: boolean;
  results_id: string | null;
  sample_collection_timestamp: string | null;
  results_timestamp: number | null;
  notes: string | null;
  clinic_id: string;
}

export interface ClinicInfo {
  clinic_id?: string;
  name: string;
  address: string;
  contact_phone: string | null;
  contact_email: string | null;
}

export interface TestResult {
  id: string;
  result: string;
  // Add other result fields
}

// Get provider profile
export async function getProviderProfile() {
  return authenticatedRequest<any>('/v1/private/provider/profile');
}

// Get clinic info
export async function getClinicInfo() {
  return authenticatedRequest<ClinicInfo>('/v1/private/provider/clinic');
}

// Get kits with optional filtering
export async function getKits(page = 1, pageSize = 10, filters: { search?: string; status?: string } = {}) {
  // Build query string
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('pageSize', pageSize.toString());

  if (filters.search) {
    params.append('search', filters.search);
  }

  if (filters.status) {
    params.append('status', filters.status);
  }

  return authenticatedRequest<PaginatedResponse<Kit>>(
    `/v1/private/provider/kits?${params.toString()}`
  );
}

// Types for the API responses
export interface KitResponse {
  kit: {
    tracking_number_changes: any[];
    notes: string | null;
    has_results: boolean;
    results_id: string | null;
    results_timestamp: number | null;
    results_laboratory_employee_id: string | null;
    sample_collection_timestamp: string | null;
    tracking_number: string;
    courier: string;
    tracking_integration: string;
    tracking_integration_tracking_id: string;
    tracking_integration_delivery_status: string | null;
    serial_number: string;
    has_new_warnings: boolean;
    has_warnings: boolean;
    lot_year: number;
    lot_number: string;
    status: string;
    claimed: boolean;
    claim_timestamp: number;
    customer_id: string;
    customer_email: string;
    clinic_id: string;
    created_at: number;
    updated_at: number;
    is_deleted: boolean;
    deleted_at: number | null;
  };
  results: any;
}

export interface TestResultResponse {
  success: boolean;
  message: string;
  data: {
    current: {
      result: string;
      id: string;
    };
    history: Array<{
      data: {
        result: string;
        id: string;
      };
      metadata: {
        version: number;
        txTime: string;
      };
    }>;
  };
}

// Get kit details
export async function getKitDetails(serialNumber: string) {
  return authenticatedRequest<KitResponse>(`/v1/private/provider/kits/${serialNumber}`);
}

// Get test results
export async function getTestResults(resultId: string) {
  return authenticatedRequest<TestResultResponse>(`/v1/private/provider/results/${resultId}`);
}

/**
 * Fetches the combined kit, customer, and latest result information.
 * Handles the API wrapper and returns the unwrapped data payload.
 * @param serialNumber - The serial number of the kit.
 */
export async function getEnrichedResult(serialNumber: string): Promise<EnrichedResultResponse> { // Function returns the unwrapped data
  if (!serialNumber) {
    throw new Error("Serial number is required to fetch enriched result.");
  }
  // Ensure the endpoint matches your Koa router definition
  const endpoint = `/v1/private/provider/kits/${serialNumber}/enriched-result`;

  // Call the generic request function, expecting the *wrapped* response structure
  const response = await authenticatedRequest<ApiResponseWrapper<EnrichedResultResponse>>(endpoint);

  // Check the wrapper for success and presence of data
  if (response && response.success && response.data) {
    // Return the nested 'data' object which matches EnrichedResultResponse
    return response.data;
  } else {
    // Throw an error using the message from the API wrapper if available
    console.error("API indicated failure or missing data:", response);
    throw new Error(response?.message || 'Failed to retrieve valid data from API.');
  }
}

// Define a custom error class to hold status and body
export class HttpError extends Error {
    status: number;
    body: any; // Can hold the parsed JSON error body

    constructor(message: string, status: number, body: any = null) {
        super(message); // Pass message to the Error base class
        this.name = 'HttpError'; // Useful for identification
        this.status = status;
        this.body = body;
         // Maintains proper stack trace in V8 environments (optional but good)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, HttpError);
        }
    }
}

// Define the data structure that fetchResults returns
export interface ResultDetails {
  result: string;
  // ... other properties
}

export interface ResultsMetadata {
  kitId: string;
  collectionDateTime: string | null;
  resultsTimeStamp: number | null;
  procedureDate: string | null;
}

export interface ResultsData {
  current: ResultDetails | null;
  history: any[];
  results: ResultsMetadata | null;
  customerDetails: {
    given_name: string;
    family_name: string;
    birthdate: string | null;
  }
}

export const fetchResults = async (serialNumber: string): Promise<ResultsData> => {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/semen-analysis-kits/${serialNumber}/customerResults`;

    try {
        const token = await AuthProvider.getValidAuthorizationToken(); // Ensure token is awaited
        const response = await fetch(apiUrl, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`, // Use the retrieved token
            },
            method: "GET",
        });

        // --- Success Case ---
        if (response.ok) {
            const result = await response.json();
            // Assuming the backend nests the actual data under 'data' based on your previous component
            if (!result?.data) {
                 console.warn("API response OK, but 'data' field is missing or undefined.", result);
                 // Throw an error or return null/empty object based on expected handling
                throw new Error("Received unexpected data structure from API.");
            }
            return result.data;
        }

        // --- Error Case (!response.ok) ---

        let errorBody: any = null;
        let errorMessage = `Request failed with status ${response.status}`; // Default message

        // Try to parse the response body as JSON, it might contain useful error details
        try {
            errorBody = await response.json();
            // Use message from backend response if available
            if (errorBody && typeof errorBody.message === 'string') {
                errorMessage = errorBody.message;
            } else if (errorBody && typeof errorBody.error === 'string') {
                 // Fallback check for 'error' field if 'message' isn't there
                errorMessage = errorBody.error;
            }
        } catch (jsonError) {
            // If response body is not JSON or empty, log warning but proceed
            console.warn(`Could not parse error response body for status ${response.status}:`, jsonError);
            // Use status text as a fallback message if parsing failed
            errorMessage = `Request failed with status ${response.status}: ${response.statusText}`;
        }

        // Specific handling for 422 Validation Errors, using parsed body if available
        if (response.status === 422 && errorBody?.errors) {
            errorMessage = `Validation error: ${JSON.stringify(errorBody.errors)}`;
             // Throw the custom HttpError, including status and body
            throw new HttpError(errorMessage, response.status, errorBody);
        }

        // For ALL non-ok responses (including 403, 404, 500, etc.)
        // Throw the custom HttpError, including status and body
        throw new HttpError(errorMessage, response.status, errorBody);

    } catch (error) {
        // Log the error regardless of its type (Network error, HttpError, etc.)
        console.error("Error during fetchResults execution:", error);

        // If it's already our custom HttpError or a generic Error, re-throw it
        // If it's something else, wrap it or handle appropriately if needed
        // For simplicity, we just re-throw here.
        throw error;
    }

    // This line is unreachable if success returns and errors throw, which is good.
    // It's kept here only if you modify logic to potentially not throw on certain errors.
};

// RSA Order kit for a customer
export const rsaorderKit = async (
    customerData: {
        firstName: string;
        lastName: string;
        phoneNumber: string;
        dateOfBirth: string;
        ssnLast4: string;
        address: string;
        addressLine2: string;
        city: string;
        state: string;
        zip: string;
    },
    providerId: string,
    clinicId?: string,
    clinicName?: string
): Promise<any> => {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/provider/create-rsa-kits-order`;

    try {
      return await authorizedJsonFetch(
        apiUrl,
        {
          method: "POST",
          body: JSON.stringify({
            customer_first_name: customerData.firstName,
            customer_last_name: customerData.lastName,
            customer_phone: customerData.phoneNumber,
            customer_birthdate: customerData.dateOfBirth,
            customer_ssn_last4: customerData.ssnLast4 || null,
            customer_address: customerData.address,
            customer_address_line2: customerData.addressLine2,
            customer_city: customerData.city,
            customer_state: customerData.state,
            customer_zip: customerData.zip,
            provider_id: providerId,
            clinic_id: clinicId,
            clinic_name: clinicName,
            year: new Date().getFullYear(),
            size: 1,
          }),
        },
        { success: true, message: "Kit rsaordered successfully" }
      );
    } catch (error) {
      console.error("Error during rsaorderKit execution:", error);
      throw error;
    }
};

// Update RSA kit customer details
export const updateRsaKitCustomer = async (
    serialNumber: string,
    customerData: {
        firstName: string;
        lastName: string;
        phoneNumber: string;
        dateOfBirth: string;
        ssnLast4: string;
        address: string;
        addressLine2: string;
        city: string;
        state: string;
        zip: string;
    }
): Promise<any> => {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/provider/rsa-kits/${serialNumber}/customer`;

    try {
      return await authorizedJsonFetch(
        apiUrl,
        {
          method: "PATCH",
          body: JSON.stringify({
            customer_first_name: customerData.firstName,
            customer_last_name: customerData.lastName,
            customer_phone: customerData.phoneNumber,
            customer_birthdate: customerData.dateOfBirth,
            customer_ssn_last4: customerData.ssnLast4 || null,
            customer_address: customerData.address,
            customer_address_line2: customerData.addressLine2,
            customer_city: customerData.city,
            customer_state: customerData.state,
            customer_zip: customerData.zip,
          }),
        },
        { success: true, message: "Customer details updated successfully" }
      );
    } catch (error) {
      console.error("Error during updateRsaKitCustomer execution:", error);
      throw error;
    }
};